/* DashboardTab — analytics view for one vertical (PS.tabDashboard + dashWidgets).
   Courier + month filter bar, 7-KPI strip, month-wise pendency summary, status
   donut + raised-vs-resolved trend, pendency-by-courier, top dispute reasons. */
import React, {useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {useNavigate} from 'react-router-dom'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {KpiStrip, SearchSelect, inr, money} from '../components'
import {
  RECON,
  isOpen,
  isClosed,
  isDisputed,
  shortStatus,
  monthsOf,
  couriersOf,
  VertChip,
} from './reconShared'
import {Donut, MiniBar, TrendBars} from './charts'

const ICONS = {
  receipt: <ReceiptLongOutlinedIcon />,
  warning: <WarningAmberOutlinedIcon />,
  check: <CheckCircleOutlinedIcon />,
  clock: <AccessTimeOutlinedIcon />,
  money: <PaymentsOutlinedIcon />,
  history: <HistoryOutlinedIcon />,
  sync: <SyncOutlinedIcon />,
}

export default function DashboardTab({invoices}) {
  const navigate = useNavigate()
  const [courier, setCourier] = useState('All')
  const [month, setMonth] = useState('All')

  const couriers = couriersOf(invoices)
  const allMonths = monthsOf(invoices)
  const curCourier = couriers.indexOf(courier) < 0 ? 'All' : courier
  const curMonth = month !== 'All' && !allMonths.some((m) => m.k === month) ? 'All' : month

  // courier-filtered (month-summary uses this); then + month filter (everything else)
  const base = useMemo(
    () => invoices.filter((i) => curCourier === 'All' || i.partner === curCourier),
    [invoices, curCourier]
  )
  const fr = useMemo(
    () => base.filter((i) => curMonth === 'All' || i.monthKey === curMonth),
    [base, curMonth]
  )

  // KPIs
  const invoiced = fr.reduce((s, i) => s + i.billedAmount, 0)
  const disputedTotal = fr.reduce((s, i) => s + i.disputedAmount, 0)
  const openVal = fr.filter(isOpen).reduce((s, i) => s + i.disputedAmount, 0)
  const cnRecovered = fr.reduce((s, i) => s + (i.cnAmount || 0), 0)
  const cnPendingVal = fr
    .filter((i) => i.reconStatus === 'CN Pending')
    .reduce((s, i) => s + i.disputedAmount, 0)
  const openCount = fr.filter(isOpen).length
  const resolvedArr = fr.filter((i) => i.resolutionDays != null)
  const avgRes = resolvedArr.length
    ? Math.round(resolvedArr.reduce((s, i) => s + i.resolutionDays, 0) / resolvedArr.length)
    : 0
  const recovery = disputedTotal ? Math.round((cnRecovered * 100) / disputedTotal) : 0
  const netPayable = invoiced - cnRecovered

  const kpiCards = [
    {
      label: 'Total Invoiced',
      value: money(invoiced),
      icon: ICONS.receipt,
      accent: '#1976D2',
      sub: fr.length + ' invoices',
    },
    {
      label: 'Open Disputes',
      value: openCount,
      icon: ICONS.warning,
      accent: openCount ? '#B45309' : '#6B7280',
      sub: money(openVal) + ' under dispute',
      onClick: () => navigate('/supply/invoice-recon?tab=disputes'),
    },
    {
      label: 'CN Recovered',
      value: money(cnRecovered),
      icon: ICONS.check,
      accent: '#0F766E',
      sub: 'credit notes received',
    },
    {
      label: 'CN Pending',
      value: money(cnPendingVal),
      icon: ICONS.clock,
      accent: '#6A1B9A',
      sub: 'accepted, awaiting CN',
    },
    {
      label: 'Net Payable',
      value: money(netPayable),
      icon: ICONS.money,
      accent: '#1565C0',
      sub: 'invoiced − CN recovered',
    },
    {
      label: 'Avg Resolution',
      value: avgRes,
      unit: 'd',
      icon: ICONS.history,
      accent: '#637381',
      sub: resolvedArr.length + ' disputes resolved',
    },
    {
      label: 'Recovery Rate',
      value: recovery + '%',
      icon: ICONS.sync,
      accent: '#2E7D32',
      sub: 'CN ÷ disputed value',
    },
  ]

  // month-wise pendency (courier-filtered only, all months)
  const months = monthsOf(base)
  const monthRows = months.map((m) => {
    const mi = base.filter((i) => i.monthKey === m.k)
    const inv = mi.reduce((s, i) => s + i.billedAmount, 0)
    const disp = mi.reduce((s, i) => s + i.disputedAmount, 0)
    const rec = mi.reduce((s, i) => s + (i.cnAmount || 0), 0)
    const pend = mi.filter(isOpen).reduce((s, i) => s + i.disputedAmount, 0)
    const rejected = mi
      .filter((i) => i.reconStatus === 'Rejected')
      .reduce((s, i) => s + i.disputedAmount, 0)
    return {m, inv, disp, rec, pend, rejected, open: mi.filter(isOpen).length, n: mi.length}
  })

  // status donut
  const sc = {}
  RECON.forEach((r) => (sc[r.k] = 0))
  fr.forEach((i) => (sc[i.reconStatus] = (sc[i.reconStatus] || 0) + 1))
  const donutSegs = RECON.map((r) => ({label: shortStatus(r.k), value: sc[r.k], color: r.c}))

  // raised vs resolved trend
  const trendMonths = monthsOf(base)
  const trend = {}
  trendMonths.forEach((m) => (trend[m.k] = {raised: 0, resolved: 0, label: m.label}))
  base.forEach((i) => {
    if (i.raisedDate) {
      const rk = i.raisedDate.slice(0, 7)
      if (trend[rk]) trend[rk].raised++
    }
    if (i.resolvedDate) {
      const sk = i.resolvedDate.slice(0, 7)
      if (trend[sk]) trend[sk].resolved++
    }
  })

  // pendency by courier
  const byC = {}
  fr.forEach((i) => {
    const o =
      byC[i.partner] ||
      (byC[i.partner] = {
        partner: i.partner,
        vertical: i.vertical,
        inv: 0,
        disp: 0,
        rec: 0,
        pend: 0,
        n: 0,
        closed: 0,
      })
    o.inv += i.billedAmount
    o.disp += i.disputedAmount
    o.rec += i.cnAmount || 0
    if (isOpen(i)) o.pend += i.disputedAmount
    o.n++
    if (isClosed(i) || i.reconStatus === 'No Dispute') o.closed++
  })
  const cList = Object.values(byC).sort((a, b) => b.pend - a.pend)
  const maxPend = Math.max(1, ...cList.map((o) => Math.max(o.disp, 1)))

  // reason analytics
  const byR = {}
  fr.filter(isDisputed).forEach((i) => {
    if (!i.reason) return
    const o = byR[i.reason] || (byR[i.reason] = {reason: i.reason, val: 0, n: 0})
    o.val += i.disputedAmount
    o.n++
  })
  const rList = Object.values(byR).sort((a, b) => b.val - a.val)
  const maxR = Math.max(1, ...rList.map((o) => o.val))

  const recColor = (p) => (p >= 80 ? '#2E7D32' : p >= 50 ? '#B45309' : '#C62828')

  return (
    <>
      <div className='filtbar'>
        <div className='seg'>
          <label>Courier</label>
          <div style={{minWidth: 200}}>
            <SearchSelect
              value={curCourier}
              onChange={(v) => setCourier(v || 'All')}
              options={[{value: 'All', label: 'All couriers'}, ...couriers.map((c) => ({value: c, label: c}))]}
              placeholder='Search…'
            />
          </div>
        </div>
        <div className='seg'>
          <label>Month</label>
          <div style={{minWidth: 200}}>
            <SearchSelect
              value={curMonth}
              onChange={(v) => setMonth(v || 'All')}
              options={[
                {value: 'All', label: 'All months'},
                ...allMonths.map((m) => ({value: m.k, label: m.label})),
              ]}
              placeholder='Search…'
            />
          </div>
        </div>
        <span className='hlp' style={{marginLeft: 4}}>
          Courier filter applies to every widget; the month filter applies to all except the
          month-wise summary.
        </span>
      </div>

      <KpiStrip className='fit150' cards={kpiCards} />

      {/* Month-wise pendency summary */}
      <div className='card' style={{marginTop: 16}}>
        <div className='card-hd'>
          <h3>Month-wise Pendency Summary</h3>
          <span className='muted' style={{fontSize: 12}}>
            {curCourier === 'All' ? 'all couriers' : curCourier} · invoiced vs disputed → recovered
            / pending
          </span>
        </div>
        <div className='tbl-wrap'>
          <table className='tbl'>
            <thead>
              <tr>
                <th>Month</th>
                <th style={{textAlign: 'right'}}>Invoiced</th>
                <th style={{textAlign: 'right'}}>Disputed</th>
                <th style={{textAlign: 'right'}}>CN Recovered</th>
                <th style={{textAlign: 'right'}}>Pending ₹</th>
                <th style={{textAlign: 'center'}}>Open</th>
                <th style={{minWidth: 160}}>Disputed split</th>
                <th style={{textAlign: 'right'}}>Recovery</th>
              </tr>
            </thead>
            <tbody>
              {monthRows.map((r) => {
                const recPct = r.disp ? Math.round((r.rec * 100) / r.disp) : 0
                return (
                  <tr key={r.m.k}>
                    <td>
                      <b>{r.m.label.replace(/^\d+\D\d+ /, '')}</b>
                    </td>
                    <td style={{textAlign: 'right'}}>{inr(r.inv)}</td>
                    <td style={{textAlign: 'right'}}>
                      {r.disp ? inr(r.disp) : <span className='muted'>—</span>}
                    </td>
                    <td style={{textAlign: 'right', color: '#0F766E', fontWeight: 600}}>
                      {r.rec ? inr(r.rec) : <span className='muted'>—</span>}
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        color: r.pend ? 'var(--warning)' : 'var(--neutral)',
                        fontWeight: 600,
                      }}
                    >
                      {r.pend ? inr(r.pend) : '—'}
                    </td>
                    <td style={{textAlign: 'center'}}>
                      {r.open ? (
                        <span className='mini-pill mp-amber'>{r.open}</span>
                      ) : (
                        <span className='muted'>0</span>
                      )}
                    </td>
                    <td>
                      {r.disp ? (
                        <MiniBar
                          segments={[
                            {label: 'Recovered', v: r.rec, color: '#0F766E'},
                            {label: 'Pending', v: r.pend, color: '#B45309'},
                            {label: 'Rejected', v: r.rejected, color: '#C62828'},
                          ]}
                        />
                      ) : (
                        <span className='muted'>—</span>
                      )}
                    </td>
                    <td style={{textAlign: 'right', fontWeight: 700, color: recColor(recPct)}}>
                      {r.disp ? recPct + '%' : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className='age-legend' style={{padding: '0 20px 14px'}}>
          <div className='li'>
            <span className='sw' style={{background: '#0F766E'}} />
            CN recovered
          </div>
          <div className='li'>
            <span className='sw' style={{background: '#B45309'}} />
            Pending
          </div>
          <div className='li'>
            <span className='sw' style={{background: '#C62828'}} />
            Rejected
          </div>
        </div>
      </div>

      {/* status donut + trend */}
      <div className='chart-2'>
        <div className='card'>
          <div className='card-hd'>
            <h3>Dispute Status Breakdown</h3>
            <span className='muted' style={{fontSize: 12}}>
              {fr.length} invoices
            </span>
          </div>
          <div className='card-pad'>
            <Donut segments={donutSegs} unit='invoices' />
          </div>
        </div>
        <div className='card'>
          <div className='card-hd'>
            <h3>Dispute Raised vs Resolved</h3>
            <span className='muted' style={{fontSize: 12}}>
              by month · is the backlog growing?
            </span>
          </div>
          <div className='card-pad'>
            <TrendBars months={trendMonths} trend={trend} />
          </div>
        </div>
      </div>

      {/* pendency by courier */}
      <div className='card' style={{marginTop: 16}}>
        <div className='card-hd'>
          <h3>Pendency by Courier</h3>
          <span className='muted' style={{fontSize: 12}}>
            recovered vs pending · reconciliation rate
          </span>
        </div>
        <div className='card-pad'>
          {cList.length ? (
            cList.map((o) => {
              const recPct = Math.round((o.closed * 100) / o.n)
              return (
                <div
                  className='barrow'
                  key={o.partner}
                  style={{gridTemplateColumns: '170px 1fr 90px 64px'}}
                >
                  <div>
                    <b>{o.partner}</b> <VertChip vertical={o.vertical} />
                  </div>
                  <div className='minibar' style={{height: 11}}>
                    {o.rec ? (
                      <span
                        title={'Recovered ' + inr(o.rec)}
                        style={{
                          width: ((o.rec / maxPend) * 100).toFixed(1) + '%',
                          background: '#0F766E',
                        }}
                      />
                    ) : null}
                    {o.pend ? (
                      <span
                        title={'Pending ' + inr(o.pend)}
                        style={{
                          width: ((o.pend / maxPend) * 100).toFixed(1) + '%',
                          background: '#B45309',
                        }}
                      />
                    ) : null}
                  </div>
                  <div
                    style={{
                      textAlign: 'right',
                      fontWeight: 700,
                      color: o.pend ? 'var(--warning)' : 'var(--neutral)',
                    }}
                  >
                    {o.pend ? inr(o.pend) : <span className='muted'>—</span>}
                  </div>
                  <div
                    style={{
                      textAlign: 'right',
                      fontWeight: 700,
                      color: recPct >= 80 ? '#2E7D32' : '#B45309',
                    }}
                  >
                    {recPct}%
                  </div>
                </div>
              )
            })
          ) : (
            <div className='muted' style={{fontSize: 13}}>
              No invoices in this view.
            </div>
          )}
        </div>
      </div>

      {/* top reasons */}
      {rList.length ? (
        <div className='card' style={{marginTop: 16}}>
          <div className='card-hd'>
            <h3>Top Dispute Reasons</h3>
            <span className='muted' style={{fontSize: 12}}>
              disputed value by reason code — spot systemic over-billing
            </span>
          </div>
          <div className='card-pad'>
            {rList.map((o) => (
              <div
                className='barrow'
                key={o.reason}
                style={{gridTemplateColumns: '200px 1fr 110px'}}
              >
                <div>
                  {o.reason}{' '}
                  <span className='muted' style={{fontSize: 11}}>
                    {'· ' + o.n}
                  </span>
                </div>
                <div className='minibar' style={{height: 11}}>
                  <span
                    title={o.reason + ' — ' + inr(o.val) + ' across ' + o.n + ' invoice(s)'}
                    style={{width: ((o.val / maxR) * 100).toFixed(1) + '%', background: '#1976D2'}}
                  />
                </div>
                <div style={{textAlign: 'right', fontWeight: 700}}>{inr(o.val)}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className='warnbox info' style={{marginTop: 16}}>
        <InfoOutlinedIcon />
        <div>
          Recovery Rate = credit-note value received ÷ total disputed value. Net Payable = billed −
          CN recovered. Only ProShip Admin can <b>Close &amp; Share with Finance</b>; commercial
          users raise and work disputes.
        </div>
      </div>
    </>
  )
}

DashboardTab.propTypes = {
  invoices: PropTypes.array,
}
